import { useCallback, useEffect, useRef, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../common/hooks';
import { Helmet } from 'react-helmet';
import { updateCookiePolicyAlertBoxClosed } from '../../features/global/globalSlice';

const ConsentManager = () => {
    const [hasConsentForAnalytics, setHasConsentForAnalytics] = useState(false);
    const [heapInitialized, setHeapInitialized] = useState(false);
    const dispatch = useAppDispatch();
    const previousAlertBoxClosed = useRef<boolean | undefined>(undefined);
    const heapScriptRef = useRef<HTMLScriptElement | null>(null);
    const oneTrustScriptRef = useRef<HTMLScriptElement | null>(null);
    const userEmail = useAppSelector((state) => state?.auth?.user?.userEmail);
    const userRole = useAppSelector((state) => state?.auth?.user?.userRole);
    const tenant = useAppSelector((state) => state?.auth?.user?.tenant);
    const currentLanguage = useAppSelector((state) => state?.global?.currentLanguage);

    const removeHeapScript = useCallback(() => {
        if (heapScriptRef.current) {
            heapScriptRef.current.remove();
            heapScriptRef.current = null;
        }

        // Also remove any heap scripts that might exist in the document
        const heapScriptSrc = getHeapScriptSrc();
        if (heapScriptSrc) {
            const existingScripts = document.querySelectorAll(`script[src="${heapScriptSrc}"]`);
            existingScripts.forEach((script) => script.remove());
        }

        // Clear heap object from window
        if (window.heap) {
            delete window.heap;
        }

        setHeapInitialized(false);
    }, []);

    const removeOneTrustScript = useCallback(() => {
        if (oneTrustScriptRef.current) {
            oneTrustScriptRef.current.remove();
            oneTrustScriptRef.current = null;
        }

        // Remove OneTrust scripts
        const scripts = document.querySelectorAll('script[src*="cookielaw.org"]');
        scripts.forEach((script) => script.remove());

        // Remove OneTrust DOM elements
        const elementsToRemove = [
            'onetrust-banner-sdk',
            'onetrust-pc-sdk',
            'onetrust-consent-sdk',
            'ot-sdk-cookie-policy'
        ];

        elementsToRemove.forEach((id) => {
            const element = document.getElementById(id);
            if (element) element.remove();
        });

        // Clear OneTrust from window
        if (window.OneTrust) delete window.OneTrust;
        if (window.OnetrustActiveGroups) delete window.OnetrustActiveGroups;
        if (window.OptanonWrapper) delete window.OptanonWrapper;
    }, []);

    const getHeapScriptSrc = useCallback(() => {
        if (process.env.REACT_APP_ENVIRONMENT === 'production') return '/heap-loader.js';
        if (process.env.REACT_APP_ENVIRONMENT === 'uat') return '/heap-loader_uat.js';
        return undefined;
    }, []);

    const checkConsent = useCallback(() => {
        const hasConsent = window.OnetrustActiveGroups && window.OnetrustActiveGroups.includes('C0002');
        setHasConsentForAnalytics(hasConsent || false);

        if (!hasConsent && window.heap) {
            window.heap.stopTracking?.(true);

            // Remove the heap script here
            removeHeapScript();
        }

        const isAlertBoxClosed = window.OneTrust?.IsAlertBoxClosed();
        if (previousAlertBoxClosed.current !== isAlertBoxClosed) {
            dispatch(updateCookiePolicyAlertBoxClosed(isAlertBoxClosed));
            previousAlertBoxClosed.current = isAlertBoxClosed;
        }

        return hasConsent;
    }, [dispatch, removeHeapScript]);

    // Track script element when it's added
    useEffect(() => {
        if (hasConsentForAnalytics && !heapInitialized) {
            const heapScriptSrc = getHeapScriptSrc();
            if (heapScriptSrc) {
                // Create and add script manually to track the reference
                const script = document.createElement('script');
                script.src = heapScriptSrc;
                script.async = true;
                script.nonce = window.__nonce || '';

                script.onload = () => {
                    const checkHeapReady = () => {
                        if (window.heap && typeof window.heap.track === 'function') {
                            if (userEmail && userRole && tenant) {
                                window.heap?.identify(userEmail);
                                window.heap?.addUserProperties({
                                    userRole,
                                    tenant
                                });
                            }
                            setHeapInitialized(true);
                            return true;
                        }
                        return false;
                    };

                    if (checkHeapReady()) return;

                    const pollInterval = setInterval(() => {
                        if (checkHeapReady()) {
                            clearInterval(pollInterval);
                        }
                    }, 100);

                    setTimeout(() => {
                        clearInterval(pollInterval);
                        console.warn('Heap initialization timeout');
                    }, 10000);
                };

                script.onerror = () => {
                    console.error('Failed to load Heap script');
                };

                // Store reference and add to DOM
                heapScriptRef.current = script;
                document.head.appendChild(script);
            }
        }
    }, [hasConsentForAnalytics, heapInitialized, getHeapScriptSrc]);

    // Load OneTrust script manually when language changes
    useEffect(() => {
        if (!currentLanguage) return;

        // Clean up existing OneTrust first
        removeOneTrustScript();

        // Update document language
        document.documentElement.lang = currentLanguage;

        console.log('Loading OneTrust for language:', currentLanguage);

        // Create and add OneTrust script
        const script = document.createElement('script');
        script.src = 'https://cdn.cookielaw.org/scripttemplates/otSDKStub.js';
        script.async = true;
        script.setAttribute('data-document-language', 'true');
        script.setAttribute('type', 'text/javascript');
        script.setAttribute('charset', 'UTF-8');
        script.setAttribute(
            'data-domain-script',
            process.env.REACT_APP_ENVIRONMENT === 'production'
                ? '0198c9f5-f7a2-7b51-a625-93764a9900a3'
                : '0198c9f5-f7a2-7b51-a625-93764a9900a3-test'
        );
        if (window.__nonce) {
            script.nonce = window.__nonce;
        }

        script.onload = () => {
            console.log('OneTrust script loaded successfully');
        };

        script.onerror = () => {
            console.error('Failed to load OneTrust script');
        };

        // Store reference and add to DOM
        oneTrustScriptRef.current = script;
        document.head.appendChild(script);

        return () => {
            // Cleanup on unmount or language change
            removeOneTrustScript();
        };
    }, [currentLanguage, removeOneTrustScript]);

    useEffect(() => {
        window.OptanonWrapper = () => {
            checkConsent();
        };

        const handleConsentUpdate = () => {
            checkConsent();
        };

        window.addEventListener('OneTrustGroupsUpdated', handleConsentUpdate);

        if (window.OnetrustActiveGroups) {
            checkConsent();
        }

        return () => {
            window.removeEventListener('OneTrustGroupsUpdated', handleConsentUpdate);
            delete window.OptanonWrapper;
        };
    }, [checkConsent]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            removeHeapScript();
            removeOneTrustScript();
        };
    }, [removeHeapScript, removeOneTrustScript]);

    return (
        <Helmet htmlAttributes={{ lang: currentLanguage || 'en' }}>
            {/* OneTrust scripts are now managed manually via useEffect */}
            {/* Heap script is now managed manually via useEffect */}
        </Helmet>
    );
};

export default ConsentManager;
